import { channelsQueryKeys } from '@communities/channels-layer/utils/query-keys.utils'
import { communityUserQueryKeys } from '@communities/community-user-layer/utils/query-keys.utils'
import { leaderboardQueryKeys } from '@communities/leaderboard-layer/utils/query-keys.utils'
import { membersQueryKeys } from '@communities/members-layer/utils/query-keys.utils'
import { postsQueryKeys } from '@communities/posts-layer/utils/query-keys.utils'

import { groupsQueryKeys } from './query-keys.utils'
import type { QueryClient } from '@tanstack/vue-query'

function isUserGroupDetailsQueryKey(query: { queryKey: readonly unknown[] }): boolean {
  const key = query.queryKey as string[]
  return key.length >= 4 && key[0] === 'community-user' && key[2] === 'user-group-details'
}

/**
 * Prefix for learning-layer course queries — keep in sync with
 * `useCoursesQueryStore` `queryKeys.all`. Inlined so groups-layer does not import
 * that store (layer boundary).
 */
const coursesQueryKeyPrefix = (groupId: string, locationId: string) => ['courses', groupId, locationId] as const

/**
 * Invalidate caches that depend on auth + current-user↔group membership.
 *
 * Used by the communities layout when login flips true (not logout — that path
 * is a no-op today). Membership purchase reuses this for the membership row +
 * channel lists, then adds course entitlement keys.
 */
export function invalidateCommunitiesMembershipSessionQueries(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    queryKey: ['community-user'],
    predicate: isUserGroupDetailsQueryKey,
  })
  void queryClient.invalidateQueries({ queryKey: channelsQueryKeys.root() })
}

/**
 * After join / paid capture while already authenticated.
 *
 * Login/logout invalidation does not cover this: the member is already logged in,
 * and entitlements (private channels, `hasImplicitAccess` on courses) change
 * without an auth flip. Reuses {@link invalidateCommunitiesMembershipSessionQueries}
 * for membership + channels, then refreshes the same course surfaces
 * `purchaseCourseMutation` invalidates in learning-layer.
 */
export function invalidateCommunitiesCachesAfterMembershipPurchase(
  queryClient: QueryClient,
  params: { groupId: string; locationId: string }
): void {
  const { groupId, locationId } = params

  // Membership row + channel lists — same helper the layout uses on login.
  invalidateCommunitiesMembershipSessionQueries(queryClient)

  // Course / learner access (mirrors learning-layer purchaseCourse onSuccess).
  void queryClient.invalidateQueries({ queryKey: coursesQueryKeyPrefix(groupId, locationId) })
  void queryClient.invalidateQueries({ queryKey: ['library'] })
  void queryClient.invalidateQueries({ queryKey: ['modules'] })
  void queryClient.invalidateQueries({ queryKey: ['learner-api'] })
}

/**
 * After leaving a group while still authenticated: session-level invalidation plus
 * group-scoped lists (posts, members, leaderboard, groups/discovery).
 */
export function invalidateCommunitiesMembershipCachesAfterLeaveGroup(
  queryClient: QueryClient,
  params: { groupId: string; locationId?: string }
): void {
  const { groupId, locationId } = params

  invalidateCommunitiesMembershipSessionQueries(queryClient)

  if (locationId) {
    void queryClient.invalidateQueries({
      queryKey: communityUserQueryKeys.userGroupDetails(groupId, locationId),
    })
  }

  void queryClient.invalidateQueries({ queryKey: communityUserQueryKeys.userGroupsListPrefix() })
  void queryClient.invalidateQueries({ queryKey: postsQueryKeys.all(groupId) })
  void queryClient.invalidateQueries({ queryKey: membersQueryKeys.all(groupId) })
  void queryClient.invalidateQueries({ queryKey: leaderboardQueryKeys.all(groupId) })
  void queryClient.invalidateQueries({ queryKey: groupsQueryKeys.groups() })
  void queryClient.invalidateQueries({ queryKey: groupsQueryKeys.pinnedGroups() })
  void queryClient.invalidateQueries({ queryKey: groupsQueryKeys.groupScoped(groupId) })
  void queryClient.invalidateQueries({ queryKey: groupsQueryKeys.discoveryAll() })
}
